import clsx from 'clsx';
import { ArrowDown, ArrowUp, Copy, FolderInput, Plus, Save, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BILLING_TYPES, type BillingType } from '@pz/core';
import type { ProcOutput } from '@pz/server';
import { useCategories } from '../lib/categories';
import { useM, useQ } from '../lib/hooks';
import { ProjectCombobox } from './entry';
import { Button, Checkbox, ConfirmModal, EmptyState, ErrorBox, Field, IconButton, Input, Loading, Modal, Notice, Pill, Select, td, th, useToast } from './ui';

/**
 * Templates of manual tasks (request 11 of round 4). The office keeps a few lists of standard tasks (e.g. 20 to 30 for
 * a TGA project); in a new project they are taken over in one step (project > tasks > "Aus Vorlage"). Each task is
 * created by the normal route, so the rights of the project apply; titles the project has already are left out.
 */

type Template = ProcOutput<'taskTemplates.list'>['templates'][number];
type Item = Template['items'][number];
interface Row {
  key: string;
  title: string;
  category: string;
  description: string;
  plan: string;
  billing: BillingType | '';
  fixed: boolean;
}

let seq = 0;
const nextKey = () => `r${++seq}`;
const toRow = (i: Item): Row => ({
  key: nextKey(),
  title: i.title,
  category: i.category,
  description: i.description ?? '',
  plan: i.plannedMinutes ? String(i.plannedMinutes / 60).replace('.', ',') : '',
  billing: (i.defaultBillingType as BillingType | null) ?? '',
  fixed: !!i.billingTypeFixed,
});
const planMinutes = (s: string) => (s.trim() ? Math.round(Number(s.replace(',', '.')) * 60) : null);

/** Verwaltung > Aufgabenvorlagen: the templates of the company (projects.manage). */
export function TaskTemplatesTab() {
  const { t } = useTranslation();
  const toast = useToast();
  const cats = useCategories();
  const q = useQ('taskTemplates.list', {});
  const save = useM('taskTemplates.save', { invalidate: ['taskTemplates.'] });
  const del = useM('taskTemplates.delete', { invalidate: ['taskTemplates.'] });
  const projects = useQ('projects.list', {});
  const [current, setCurrent] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [rows, setRows] = useState<Row[]>([]);
  const [fromProject, setFromProject] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const fetchFrom = useQ('taskTemplates.fromProject', { projectId: fromProject }, { enabled: !!fromProject, gcTime: 0 });
  const templates = q.data?.templates ?? [];
  const open = (tpl: Template | null) => {
    setCurrent(tpl?.id ?? 'new');
    setName(tpl?.name ?? '');
    setRows(tpl ? tpl.items.map(toRow) : [{ key: nextKey(), title: '', category: 'projektarbeit', description: '', plan: '', billing: '', fixed: false }]);
  };
  // lines from a project are added below the lines that are there
  useEffect(() => {
    if (!fetchFrom.data) return;
    setRows((r) => [...r.filter((x) => x.title.trim()), ...fetchFrom.data.items.map(toRow)].slice(0, 100));
    toast.ok(t('templates.fromProjectDone', { count: fetchFrom.data.items.length }));
    setFromProject('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchFrom.data]);
  const set = (key: string, patch: Partial<Row>) => setRows((r) => r.map((x) => (x.key === key ? { ...x, ...patch } : x)));
  const move = (i: number, d: number) =>
    setRows((r) => {
      const n = [...r];
      const [x] = n.splice(i, 1);
      n.splice(Math.max(0, Math.min(n.length, i + d)), 0, x);
      return n;
    });
  const valid = name.trim().length >= 2 && rows.length > 0 && rows.every((r) => r.title.trim().length >= 2 && (planMinutes(r.plan) === null || Number.isFinite(planMinutes(r.plan))));
  const duplicates = useMemo(() => {
    const seen = new Set<string>();
    const out = new Set<string>();
    for (const r of rows) {
      const k = r.title.trim().toLowerCase();
      if (k && seen.has(k)) out.add(k);
      seen.add(k);
    }
    return out;
  }, [rows]);
  const doSave = () =>
    save
      .mutateAsync({
        id: current && current !== 'new' ? current : undefined,
        name: name.trim(),
        items: rows.map((r) => ({
          title: r.title.trim(),
          category: r.category,
          description: r.description.trim() || null,
          plannedMinutes: planMinutes(r.plan),
          defaultBillingType: r.billing || null,
          billingTypeFixed: !!r.billing && r.fixed,
        })),
      })
      .then((res) => {
        setCurrent(res.id);
        toast.ok(t('common.saved'));
      })
      .catch(() => {});
  const groups = [
    { key: 'projekt', label: t('categories.groups.projekt') },
    { key: 'intern', label: t('categories.groups.intern') },
  ];
  return (
    <div className="flex flex-col gap-3">
      <Notice tone="neutral">{t('templates.hint')}</Notice>
      <div className="flex flex-col gap-4 lg:flex-row">
        <div className="w-full shrink-0 rounded-lg border border-line bg-sheet lg:w-72">
          <div className="flex items-center justify-between border-b border-line px-3 py-2">
            <h2 className="text-sm font-semibold">{t('templates.title')}</h2>
            <Button size="sm" icon={<Plus className="size-3.5" />} onClick={() => open(null)}>
              {t('templates.new')}
            </Button>
          </div>
          {q.isLoading && <Loading />}
          <ErrorBox error={q.error} />
          {q.data && templates.length === 0 && <p className="px-3 py-3 text-xs text-graphite">{t('templates.empty')}</p>}
          <ul>
            {templates.map((tpl) => (
              <li key={tpl.id}>
                <button
                  type="button"
                  onClick={() => open(tpl)}
                  className={clsx('flex w-full items-center justify-between gap-2 border-b border-line/60 px-3 py-2 text-left text-sm hover:bg-sheet-2', current === tpl.id && 'bg-accent-soft')}
                >
                  <span className="min-w-0 truncate font-medium">{tpl.name}</span>
                  <Pill>{t('templates.count', { count: tpl.items.length })}</Pill>
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="min-w-0 flex-1">
          {!current ? (
            <EmptyState>{t('templates.pick')}</EmptyState>
          ) : (
            <div className="flex flex-col gap-3 rounded-lg border border-line bg-sheet p-3">
              <div className="flex flex-wrap items-end gap-3">
                <Field label={t('templates.name')} className="min-w-64 flex-1">
                  <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder={t('templates.namePlaceholder')} />
                </Field>
                <Field label={t('templates.fromProject')} hint={t('templates.fromProjectHint')} className="min-w-64 flex-1">
                  <ProjectCombobox
                    allLabel={t('common.select')}
                    value={fromProject}
                    onChange={setFromProject}
                    projects={(projects.data?.projects ?? []).filter((p) => !p.archived).map((p) => ({ id: p.id, number: p.number, name: p.name, formaName: p.formaName }))}
                  />
                </Field>
              </div>
              <div className="scroll-x">
                <table className="w-full min-w-[900px] text-sm">
                  <thead className="border-b border-line bg-sheet-2">
                    <tr>
                      <th className={clsx(th, 'w-8')}>#</th>
                      <th className={th}>{t('common.name')}</th>
                      <th className={clsx(th, 'w-56')}>{t('tasks.category')}</th>
                      <th className={clsx(th, 'w-48')}>{t('entry.billingType')}</th>
                      <th className={clsx(th, 'w-24')}>{t('projectKpi.planHoursLabel')}</th>
                      <th className={clsx(th, 'w-28')} />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={r.key} className="border-b border-line/70 align-top">
                        <td className={clsx(td, 'num text-graphite')}>{i + 1}</td>
                        <td className={td}>
                          <Input aria-label={t('common.name')} value={r.title} onChange={(e) => set(r.key, { title: e.target.value })} maxLength={200} />
                          {duplicates.has(r.title.trim().toLowerCase()) && <p className="mt-1 text-xs text-warn">{t('templates.duplicate')}</p>}
                          <Input className="mt-1" aria-label={t('common.description')} placeholder={t('templates.descriptionPlaceholder')} value={r.description} onChange={(e) => set(r.key, { description: e.target.value })} maxLength={2000} />
                        </td>
                        <td className={td}>
                          <Select aria-label={t('tasks.category')} value={r.category} onChange={(e) => set(r.key, { category: e.target.value })}>
                            {groups.map((g) => (
                              <optgroup key={g.key} label={g.label}>
                                {cats.list
                                  .filter((c) => c.group === g.key && (c.active || c.key === r.category))
                                  .map((c) => (
                                    <option key={c.key} value={c.key}>
                                      {cats.label(c.key)}
                                    </option>
                                  ))}
                              </optgroup>
                            ))}
                          </Select>
                        </td>
                        <td className={td}>
                          <Select aria-label={t('entry.billingType')} value={r.billing} onChange={(e) => set(r.key, { billing: e.target.value as BillingType | '' })}>
                            <option value="">{t('templates.billingDefault')}</option>
                            {BILLING_TYPES.map((b) => (
                              <option key={b} value={b}>
                                {t(`billingType.${b}`)}
                              </option>
                            ))}
                          </Select>
                          {r.billing && <Checkbox className="mt-1" label={t('templates.fixed')} checked={r.fixed} onChange={(e) => set(r.key, { fixed: e.target.checked })} />}
                        </td>
                        <td className={td}>
                          <Input aria-label={t('projectKpi.planHoursLabel')} inputMode="decimal" className="num" value={r.plan} onChange={(e) => set(r.key, { plan: e.target.value })} />
                        </td>
                        <td className={clsx(td, 'text-right whitespace-nowrap')}>
                          <IconButton label={t('quick.moveUp')} icon={<ArrowUp className="size-3.5" />} disabled={i === 0} onClick={() => move(i, -1)} />
                          <IconButton label={t('quick.moveDown')} icon={<ArrowDown className="size-3.5" />} disabled={i === rows.length - 1} onClick={() => move(i, 1)} />
                          <IconButton label={t('templates.removeRow')} icon={<Trash2 className="size-3.5" />} onClick={() => setRows((x) => x.filter((y) => y.key !== r.key))} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  icon={<Plus className="size-3.5" />}
                  disabled={rows.length >= 100}
                  onClick={() => setRows((r) => [...r, { key: nextKey(), title: '', category: r[r.length - 1]?.category ?? 'projektarbeit', description: '', plan: '', billing: '', fixed: false }])}
                >
                  {t('templates.addRow')}
                </Button>
                <span className="text-xs text-graphite">{t('templates.rowsHint', { count: rows.length })}</span>
                <span className="ml-auto flex gap-2">
                  {current !== 'new' && (
                    <Button variant="ghost" className="text-bad" icon={<Trash2 className="size-4" />} onClick={() => setConfirmDelete(true)}>
                      {t('common.delete')}
                    </Button>
                  )}
                  <Button variant="primary" icon={<Save className="size-4" />} disabled={!valid} loading={save.isPending} onClick={doSave}>
                    {t('common.save')}
                  </Button>
                </span>
              </div>
              <ErrorBox error={save.error ?? fetchFrom.error} />
            </div>
          )}
        </div>
      </div>
      <ConfirmModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={t('templates.deleteTitle', { name })}
        text={t('templates.deleteText')}
        confirmLabel={t('common.delete')}
        danger
        loading={del.isPending}
        error={del.error}
        onConfirm={() =>
          current &&
          current !== 'new' &&
          del
            .mutateAsync({ id: current })
            .then(() => {
              setConfirmDelete(false);
              setCurrent(null);
              toast.ok(t('templates.deleted'));
            })
            .catch(() => {})
        }
      />
    </div>
  );
}

/** Project > Aufgaben > "Aus Vorlage": choose a template and its lines; titles the project has already are marked. */
export function ApplyTemplateDialog({ open, onClose, projectId }: { open: boolean; onClose: () => void; projectId: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const cats = useCategories(open);
  const q = useQ('taskTemplates.list', {}, { enabled: open });
  const apply = useM('taskTemplates.apply', { invalidate: ['tasks.', 'projects.', 'analysis.'] });
  const [id, setId] = useState('');
  const [chosen, setChosen] = useState<Set<number>>(new Set());
  const templates = q.data?.templates ?? [];
  const tpl = templates.find((x) => x.id === id);
  // the manual tasks the project has (without the filters of the list): their titles are not taken twice
  const existing = useQ('tasks.list', { projectId, source: 'manual' }, { enabled: open });
  const have = useMemo(() => new Set((existing.data?.tasks ?? []).filter((x) => !x.archived).map((x) => x.title.trim().toLowerCase())), [existing.data]);
  useEffect(() => {
    if (open && !id && templates.length) setId(templates[0].id);
  }, [open, id, templates]);
  useEffect(() => {
    setChosen(new Set((tpl?.items ?? []).map((it, i) => (have.has(it.title.trim().toLowerCase()) ? -1 : i)).filter((i) => i >= 0)));
  }, [tpl, have]);
  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title={t('templates.applyTitle')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            icon={<FolderInput className="size-4" />}
            disabled={!tpl || chosen.size === 0}
            loading={apply.isPending}
            onClick={() =>
              tpl &&
              apply
                .mutateAsync({ projectId, templateId: tpl.id, indexes: [...chosen] })
                .then((r) => {
                  toast.ok(t('templates.applied', { count: r.created.length }));
                  if (r.skipped.length) toast.warn(t('templates.appliedSkipped', { count: r.skipped.length }));
                  onClose();
                })
                .catch(() => {})
            }
          >
            {t('templates.applyN', { count: chosen.size })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {q.isLoading && <Loading />}
        <ErrorBox error={q.error} />
        {q.data && templates.length === 0 && <Notice tone="neutral">{t('templates.noneYet')}</Notice>}
        {templates.length > 0 && (
          <Field label={t('templates.title')}>
            <Select value={id} onChange={(e) => setId(e.target.value)}>
              {templates.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name} ({t('templates.count', { count: x.items.length })})
                </option>
              ))}
            </Select>
          </Field>
        )}
        {tpl && (
          <>
            <p className="text-xs text-graphite">{t('templates.applyHint')}</p>
            <ul className="max-h-96 overflow-y-auto rounded-md border border-line">
              {tpl.items.map((it, i) => {
                const dup = have.has(it.title.trim().toLowerCase());
                return (
                  <li key={i} className={clsx('flex items-start gap-3 border-b border-line/60 px-3 py-2 last:border-b-0', dup && 'bg-sheet-2 text-graphite')}>
                    <input
                      type="checkbox"
                      className="mt-1 size-4 accent-[var(--accent)]"
                      aria-label={it.title}
                      disabled={dup}
                      checked={chosen.has(i)}
                      onChange={(e) => {
                        const next = new Set(chosen);
                        if (e.target.checked) next.add(i);
                        else next.delete(i);
                        setChosen(next);
                      }}
                    />
                    <div className="min-w-0 flex-1 text-sm">
                      <span className="font-medium">{it.title}</span>
                      <span className="block text-xs text-graphite">
                        {cats.label(it.category)}
                        {it.plannedMinutes ? ` · ${t('picker.plan', { hours: String(Math.round(it.plannedMinutes / 6) / 10).replace('.', ',') })}` : ''}
                        {it.defaultBillingType ? ` · ${t(`billingType.${it.defaultBillingType as BillingType}`)}` : ''}
                      </span>
                    </div>
                    {dup && (
                      <Pill>
                        <Copy className="size-3" />
                        {t('templates.exists')}
                      </Pill>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}
        <ErrorBox error={apply.error} />
      </div>
    </Modal>
  );
}
